import { useNavigate, useRouterState } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { getAttribution } from "@/lib/attribution";
import { FIRM } from "@/lib/firm";
import { submitExecutorIntake } from "@/lib/executor-intake.functions";

const ROLES = [
  { value: "named-executor", label: "I'm named as executor in the will" },
  { value: "next-of-kin", label: "There's no will — I'm the next of kin" },
  { value: "beneficiary", label: "I'm a beneficiary" },
  { value: "adviser", label: "Accountant, adviser or financial planner" },
  { value: "unsure", label: "Not sure yet" },
] as const;

const WILL_STATUS = [
  { value: "original-held-by-me", label: "I have the original signed will" },
  { value: "original-with-firm", label: "The original is held by a law firm, bank or the Public Trustee" },
  { value: "copy-only", label: "I only have a copy or a scan" },
  { value: "no-will", label: "There is no will" },
  { value: "unsure", label: "I don't know where the will is" },
] as const;

const VALUES = [
  { value: "under-100k", label: "Under $100,000" },
  { value: "100k-500k", label: "$100,000 – $500,000" },
  { value: "500k-1m", label: "$500,000 – $1 million" },
  { value: "1m-plus", label: "Over $1 million" },
  { value: "unsure", label: "Not sure" },
] as const;

const PROPERTY = [
  { value: "sole-name", label: "Yes — in their sole name" },
  { value: "joint-tenants", label: "Yes — owned jointly with someone else" },
  { value: "tenants-in-common", label: "Yes — as tenants in common" },
  { value: "none", label: "No real property" },
  { value: "unsure", label: "Not sure" },
] as const;

const DISPUTE = [
  { value: "no", label: "No, everyone is on the same page" },
  { value: "possible", label: "Possibly — there's some tension" },
  { value: "yes", label: "Yes, someone has raised a claim" },
  { value: "unsure", label: "Not sure" },
] as const;

const LOCATIONS = [
  { value: "qld", label: "Queensland" },
  { value: "interstate", label: "Another Australian state" },
  { value: "overseas", label: "Overseas" },
  { value: "unsure", label: "Not sure" },
] as const;

const QLD_ASSETS = [
  "House or unit in Queensland",
  "Vacant land in Queensland",
  "Queensland bank account",
  "Shares held through an Australian broker",
  "Australian superannuation",
  "Australian life insurance",
  "Business or company interest in Queensland",
  "No Queensland assets",
];

const ASSETS = [
  "House or unit",
  "Vacant land",
  "Bank accounts",
  "Shares or investments",
  "Superannuation",
  "Life insurance",
  "Motor vehicle",
  "Business or company interest",
  "Debts or a mortgage",
];

const field =
  "w-full rounded-sm border border-input bg-card px-3 py-2.5 text-sm text-foreground outline-none transition-colors placeholder:text-muted-foreground/70 focus:border-ring focus:ring-2 focus:ring-ring/20";
const labelCls = "block text-sm font-medium text-foreground";

export function ExecutorIntakeForm({
  heading = "Start an estate — executor intake",
}: {
  heading?: string;
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; email?: string }>({});

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const fd = new FormData(form);

    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      role: String(fd.get("role") ?? "named-executor"),
      deceasedName: String(fd.get("deceasedName") ?? ""),
      dateOfDeath: String(fd.get("dateOfDeath") ?? ""),
      placeOfDeath: String(fd.get("placeOfDeath") ?? "qld"),
      applicantLocation: String(fd.get("applicantLocation") ?? "qld"),
      qldAssets: fd.getAll("qldAssets").map(String),
      willStatus: String(fd.get("willStatus") ?? "unsure"),
      willLocation: String(fd.get("willLocation") ?? ""),
      assets: fd.getAll("assets").map(String),
      estimatedValue: String(fd.get("estimatedValue") ?? "unsure"),
      realProperty: String(fd.get("realProperty") ?? "unsure"),
      disputeRisk: String(fd.get("disputeRisk") ?? "unsure"),
      message: String(fd.get("message") ?? ""),
      ...getAttribution(pathname),
    };

    const nextErrors: { name?: string; email?: string } = {};
    if (payload.name.trim().length < 2) nextErrors.name = "Please tell us your name";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(payload.email.trim()))
      nextErrors.email = "Please enter a valid email address";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setSending(true);
    try {
      await submitExecutorIntake({ data: payload as never });
      setSent(true);
      void navigate({ to: "/thank-you", search: { type: "executor", matter: "wills-estates" } });
      form.reset();
      toast.success("Thanks — we've received the estate details.");
    } catch {
      toast.error(`Something went wrong. Please call us on ${FIRM.phone}.`);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div className="rounded-sm border border-border bg-card p-8 text-center">
        <h2 className="font-serif text-2xl text-foreground">Thank you</h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We have the estate details and will come back to you with whether a grant is needed, the
          fixed fee and what we need from you. If it's urgent, call{" "}
          <a href={FIRM.phoneHref} className="font-semibold text-primary underline">
            {FIRM.phone}
          </a>
          .
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="rounded-sm border border-border bg-card p-6 sm:p-8" noValidate>
      <h2 className="font-serif text-2xl text-foreground">{heading}</h2>
      <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
        Five minutes now saves a phone call later. We'll tell you whether a grant of probate is
        actually needed, quote the fixed fee in writing, and list exactly what we need from you.
        Everything is handled remotely — you don't need to come in.
      </p>

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <div>
          <label className={labelCls} htmlFor="name">
            Your name
          </label>
          <input id="name" name="name" className={`${field} mt-2`} autoComplete="name" required />
          {errors.name && <p className="mt-1.5 text-xs text-destructive">{errors.name}</p>}
        </div>
        <div>
          <label className={labelCls} htmlFor="phone">
            Phone
          </label>
          <input id="phone" name="phone" className={`${field} mt-2`} autoComplete="tel" inputMode="tel" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="email">
            Email
          </label>
          <input id="email" name="email" type="email" className={`${field} mt-2`} autoComplete="email" required />
          {errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="role">
            Your role in the estate
          </label>
          <select id="role" name="role" className={`${field} mt-2`} defaultValue="named-executor">
            {ROLES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls} htmlFor="deceasedName">
            Name of the deceased
          </label>
          <input id="deceasedName" name="deceasedName" className={`${field} mt-2`} />
        </div>
        <div>
          <label className={labelCls} htmlFor="dateOfDeath">
            Date of death
          </label>
          <input id="dateOfDeath" name="dateOfDeath" type="date" className={`${field} mt-2`} />
        </div>

        <div>
          <label className={labelCls} htmlFor="placeOfDeath">
            Where did they die?
          </label>
          <select id="placeOfDeath" name="placeOfDeath" className={`${field} mt-2`} defaultValue="qld">
            {LOCATIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="applicantLocation">
            Where do you live?
          </label>
          <select
            id="applicantLocation"
            name="applicantLocation"
            className={`${field} mt-2`}
            defaultValue="qld"
          >
            {LOCATIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <p className="sm:col-span-2 -mt-2 text-xs leading-relaxed text-muted-foreground">
          A death overseas or interstate doesn't stop us acting — if there are assets in Queensland,
          the Supreme Court here can make a grant, and we can also reseal a grant already made in
          another country or state. You don't need to be in Queensland to be the applicant.
        </p>

        <fieldset className="sm:col-span-2">
          <legend className={labelCls}>What is held in Queensland? Tick anything that applies.</legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {QLD_ASSETS.map((a) => (
              <label key={a} className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" name="qldAssets" value={a} className="h-4 w-4 rounded-sm border-input" />
                {a}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="sm:col-span-2 rounded-sm border border-border bg-secondary/40 p-5">
          <h3 className="font-serif text-lg text-foreground">The original will — safe custody</h3>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            The Supreme Court needs the original signed will, not a copy. If it's held in another
            firm's safe custody, a bank or the Public Trustee, tell us and we'll write for its
            release.
          </p>
          <div className="mt-4 grid gap-4">
            <div>
              <label className={labelCls} htmlFor="willStatus">
                Where does the will stand?
              </label>
              <select id="willStatus" name="willStatus" className={`${field} mt-2`} defaultValue="unsure">
                {WILL_STATUS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls} htmlFor="willLocation">
                Who holds it, and where?{" "}
                <span className="text-muted-foreground">(firm, bank or branch, if known)</span>
              </label>
              <input id="willLocation" name="willLocation" className={`${field} mt-2`} />
            </div>
          </div>
        </div>

        <fieldset className="sm:col-span-2">
          <legend className={labelCls}>What did the estate hold? Tick anything that applies.</legend>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {ASSETS.map((a) => (
              <label key={a} className="flex items-center gap-2 text-sm text-muted-foreground">
                <input type="checkbox" name="assets" value={a} className="h-4 w-4 rounded-sm border-input" />
                {a}
              </label>
            ))}
          </div>
        </fieldset>

        <div>
          <label className={labelCls} htmlFor="realProperty">
            Did they own real property?
          </label>
          <select id="realProperty" name="realProperty" className={`${field} mt-2`} defaultValue="unsure">
            {PROPERTY.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls} htmlFor="estimatedValue">
            Rough value of the estate
          </label>
          <select id="estimatedValue" name="estimatedValue" className={`${field} mt-2`} defaultValue="unsure">
            {VALUES.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="disputeRisk">
            Is anyone likely to contest the will or make a claim?
          </label>
          <select id="disputeRisk" name="disputeRisk" className={`${field} mt-2`} defaultValue="unsure">
            {DISPUTE.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2">
          <label className={labelCls} htmlFor="message">
            Anything else we should know?
          </label>
          <textarea id="message" name="message" rows={4} className={`${field} mt-2`} />
        </div>
      </div>

      <button
        type="submit"
        disabled={sending}
        className="mt-6 inline-flex w-full items-center justify-center rounded-sm bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-60 sm:w-auto"
      >
        {sending ? "Sending…" : "Send the estate details"}
      </button>
      <p className="mt-4 text-xs leading-relaxed text-muted-foreground">
        Sending this form does not create a lawyer–client relationship, and we run a conflict check
        before we act. Please don't include sensitive financial details until we've confirmed we can
        act for you.
      </p>
    </form>
  );
}
